import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  UserPlus, Mail, Copy, CheckCircle2, Clock, Gift,
  Send, Users, AlertCircle, Timer, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InviteFriend = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/referrals/status`);
      setStatus(res.data);
    } catch (error) {
      toast.error('Failed to load referral status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/referrals/invite`, { email: email.trim().toLowerCase() });
      toast.success(res.data.message);
      setEmail('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const handleCopyCode = () => {
    if (status?.referral_code) {
      navigator.clipboard.writeText(status.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Invite a Friend</h1>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invite-friend-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <UserPlus className="h-7 w-7 text-amber-400" />
          Invite a Friend
        </h1>
        <p className="text-slate-400 mt-2 text-sm">
          Invite fellow seafarers and you'll both receive a free week of subscription
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Invites Left</p>
            <p className="text-xl sm:text-2xl font-bold text-amber-400">{status?.remaining_this_year || 0}</p>
            <p className="text-[10px] text-slate-600 mt-1">of {status?.max_per_year || 12}/year</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Accepted</p>
            <p className="text-xl sm:text-2xl font-bold text-emerald-400">{status?.successful_this_year || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Free Days Earned</p>
            <p className="text-xl sm:text-2xl font-bold text-cyan-400">{status?.total_days_earned || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-slate-500 uppercase">Reward Per Invite</p>
            <p className="text-xl sm:text-2xl font-bold text-white">{status?.reward_days || 7} days</p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Code */}
      <Card className="bg-slate-800 border-amber-500/30">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-slate-400 mb-1">Your Referral Code</p>
              <p className="text-2xl sm:text-3xl font-bold text-amber-400 tracking-wider" data-testid="referral-code">
                {status?.referral_code || '...'}
              </p>
            </div>
            <Button onClick={handleCopyCode} variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10" data-testid="copy-code-btn">
              {copied ? <><CheckCircle2 className="h-4 w-4 mr-2" />Copied!</> : <><Copy className="h-4 w-4 mr-2" />Copy Code</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Send Invite Form */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
            <Send className="h-4 w-4 text-amber-400" />
            Send an Invitation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
            <Input type="email" placeholder="Enter their email address" value={email}
              onChange={(e) => setEmail(e.target.value)} required
              className="bg-slate-700 border-slate-600 text-white flex-1" data-testid="invite-email-input" />
            <Button type="submit" disabled={sending || !email.trim() || status?.remaining_this_year === 0}
              className="bg-amber-600 hover:bg-amber-700" data-testid="send-invite-btn">
              <Mail className="h-4 w-4 mr-2" />
              {sending ? 'Sending...' : 'Send Invite'}
            </Button>
          </form>
          <div className="flex items-start gap-2 mt-3 p-3 bg-slate-700/30 rounded-lg">
            <Timer className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-400">
              Invitations expire after <strong className="text-white">24 hours</strong>. If not accepted in time, the invite is cancelled and doesn't count against your {status?.max_per_year || 12} annual limit. Only accepted invitations earn rewards.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* How it Works */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
            <Gift className="h-4 w-4 text-amber-400" />
            How Rewards Work
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-400">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center shrink-0 text-xs text-white font-bold">1</div>
              <p>You send an invitation — the recipient receives an email with a 24-hour signup link</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center shrink-0 text-xs text-white font-bold">2</div>
              <p>If they sign up within 24 hours, you <strong className="text-white">both</strong> receive <strong className="text-amber-400">{status?.reward_days || 7} days free subscription</strong></p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center shrink-0 text-xs text-white font-bold">3</div>
              <p>If they don't sign up in time, the invitation expires — no penalty, it doesn't count against your limit</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center shrink-0 text-xs text-white font-bold">4</div>
              <p>Free weeks are applied to the end of your billing year — e.g. 12 successful invites = 12 weeks free (3 months off your annual subscription)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {status?.pending_invites?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" />
              Pending Invitations ({status.pending_invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-700">
              {status.pending_invites.map((invite, i) => (
                <div key={invite.email || `pending-${i}`} className="flex items-center justify-between p-3 sm:p-4">
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{invite.email}</p>
                    <p className="text-slate-500 text-[10px] mt-0.5">
                      Sent {new Date(invite.invited_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">
                    <Timer className="h-3 w-3 mr-1" />
                    {invite.hours_remaining > 0 ? `${invite.hours_remaining}h left` : 'Expiring...'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Accepted Invites */}
      {status?.completed_invites?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-400" />
              Accepted Invitations ({status.completed_invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-700">
              {status.completed_invites.map((invite, i) => (
                <div key={invite.email || `completed-${i}`} className="flex items-center justify-between p-3 sm:p-4">
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{invite.email}</p>
                    <p className="text-slate-500 text-[10px] mt-0.5">
                      Accepted {invite.completed_at ? new Date(invite.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    </p>
                  </div>
                  <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    +{status.reward_days}d earned
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expired Invites */}
      {status?.expired_invites?.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
              <XCircle className="h-4 w-4 text-slate-500" />
              Expired ({status.expired_invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-700">
              {status.expired_invites.map((invite, i) => (
                <div key={invite.email || `expired-${i}`} className="flex items-center justify-between p-3 sm:p-4">
                  <div className="min-w-0">
                    <p className="text-slate-400 text-sm truncate">{invite.email}</p>
                    <p className="text-slate-600 text-[10px] mt-0.5">
                      Expired {invite.expired_at ? new Date(invite.expired_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                    </p>
                  </div>
                  <Badge className="bg-slate-500/15 text-slate-500 border-slate-500/30 text-[10px]">
                    Not accepted
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terms notice */}
      <div className="flex items-start gap-2 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <AlertCircle className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-500">
          Referral rewards are subject to our <a href="/settings" className="text-amber-400 hover:underline">terms and conditions</a>. 
          Maximum {status?.max_per_year || 12} successful referrals per rolling 12-month period. 
          Free weeks are applied at the end of your billing year.
        </p>
      </div>
    </div>
  );
};

export default InviteFriend;
